import { BasePlugin, } from '@uppy/core';
import { filterFilesToEmitUploadStarted, filterFilesToUpload, getAllowedMetaFields, TaskQueue, } from '@uppy/core/utils';
import packageJson from '../package.json' with { type: 'json' };
import S3Uploader, {} from './S3Uploader.js';
import S3Companion from './s3-client/CompanionS3.js';
import S3mini from './s3-client/S3mini.js';
// ============================================================================
// Constants
// ============================================================================
const MB = 1024 * 1024;
const defaultOptions = {
    shouldUseMultipart: (file) => (file.size || 0) > 100 * MB,
    allowedMetaFields: true,
    // 6 matches browser HTTP/1.1 per-origin connection limit
    limit: 6,
};
// ============================================================================
// S3Uploader Types
// ============================================================================
export default class AwsS3 extends BasePlugin {
    static VERSION = packageJson.version;
    #s3Client;
    #queue = new TaskQueue({ concurrency: this.opts.limit });
    #uploaders = {};
    constructor(uppy, opts) {
        super(uppy, { ...defaultOptions, ...opts });
        this.type = 'uploader';
        this.id = this.opts.id || 'AwsS3';
    }
    install() {
        this.#setResumableUploadsCapability(true);
        this.#initS3Client();
        this.uppy.addUploader(this.#upload);
        this.uppy.on('cancel-all', this.#handleCancelAll);
    }
    uninstall() {
        this.#setResumableUploadsCapability(false);
        this.uppy.removeUploader(this.#upload);
        this.uppy.off('cancel-all', this.#handleCancelAll);
        this.#queue.clear();
        // Abort and clean up any in-flight uploads
        for (const fileId of Object.keys(this.#uploaders)) {
            const uploader = this.#uploaders[fileId];
            if (uploader) {
                uploader.abort();
            }
        }
    }
    #setResumableUploadsCapability = (value) => {
        const { capabilities } = this.uppy.getState();
        this.uppy.setState({
            capabilities: {
                ...capabilities,
                resumableUploads: value,
            },
        });
    };
    #handleCancelAll = () => {
        this.#setResumableUploadsCapability(true);
        this.#queue.clear();
    };
    // --------------------------------------------------------------------------
    // S3 Client Initialization
    // --------------------------------------------------------------------------
    #initS3Client() {
        if ('companionEndpoint' in this.opts) {
            if (typeof this.opts.companionEndpoint !== 'string') {
                throw new TypeError('companionEndpoint must be a string');
            }
            this.#s3Client = new S3Companion({
                companionEndpoint: this.opts.companionEndpoint,
            });
        }
        else if ('getCredentials' in this.opts) {
            if (typeof this.opts.s3Endpoint !== 'string') {
                throw new TypeError('s3Endpoint must be a string');
            }
            if (typeof this.opts.getCredentials !== 'function') {
                throw new TypeError('getCredentials must be a function');
            }
            if (this.opts.region != null && typeof this.opts.region !== 'string') {
                throw new TypeError('region must be a string');
            }
            // Mode: Temporary credentials (client-side signing)
            this.#s3Client = new S3mini({
                endpoint: this.opts.s3Endpoint,
                getCredentials: this.opts.getCredentials,
                region: this.opts.region,
            });
        }
        else if ('signRequest' in this.opts) {
            if (typeof this.opts.signRequest !== 'function') {
                throw new TypeError('signRequest must be a function');
            }
            // Mode: Custom signing function
            this.#s3Client = new S3mini({
                signRequest: this.opts.signRequest,
            });
        }
        else {
            throw new TypeError('One of options `companionEndpoint`, `signRequest`, or `getCredentials` is required');
        }
    }
    // --------------------------------------------------------------------------
    // Upload Entry Point
    // --------------------------------------------------------------------------
    #upload = async (fileIDs) => {
        if (fileIDs.length === 0)
            return;
        const files = this.uppy.getFilesByIds(fileIDs);
        const filesToUpload = filterFilesToUpload(files);
        const filesToEmit = filterFilesToEmitUploadStarted(filesToUpload);
        this.uppy.emit('upload-start', filesToEmit);
        const promises = filesToUpload.map((file) => {
            if (file.isRemote) {
                // Remote uploads are queued internally by RequestClient.uploadRemoteFile()
                // via getQueue(), so no outer queue wrapping is needed here.
                return this.#uploadRemoteFile(file);
            }
            return this.#queue.add(async () => {
                // File may have been removed while waiting in the queue.
                // Unlike actively uploading files, queued files don't have an S3Uploader
                // instance yet, so there's no event listener to catch the removal.
                // Re-fetch the file to ensure it still exists before starting upload.
                const currentFile = this.uppy.getFile(file.id);
                if (!currentFile) {
                    return;
                }
                return this.#uploadLocalFile(currentFile); // assume it's still a local file since remote files aren't queued
            });
        });
        await Promise.allSettled(promises);
        // After the upload batch is done, restore resumable uploads capability.
        // It may have been set to false if there were remote files in this batch.
        this.#setResumableUploadsCapability(true);
    };
    // --------------------------------------------------------------------------
    // Local File Upload
    // --------------------------------------------------------------------------
    async #uploadLocalFile(file) {
        try {
            return await new Promise((resolve, reject) => {
                // Create uploader (events are wired internally).
                // S3Uploader detects resume state from file.s3Multipart internally.
                const uploader = new S3Uploader({
                    uppy: this.uppy,
                    s3Client: this.#s3Client,
                    file,
                    metadata: this.#getAllowedMeta(file),
                    requestedKey: this.#generateKey(file),
                    shouldUseMultipart: this.#shouldUseMultipart(file),
                    getChunkSize: this.opts.getChunkSize,
                    log: (...args) => this.uppy.log(...args),
                    onProgress: (bytesUploaded, bytesTotal) => {
                        this.uppy.emit('upload-progress', file, {
                            uploadStarted: file.progress.uploadStarted ?? Date.now(),
                            bytesUploaded,
                            bytesTotal,
                        });
                    },
                    onPartComplete: (part) => {
                        this.uppy.emit('s3-multipart:part-uploaded', file, part);
                    },
                    onSuccess: (result) => {
                        this.uppy.emit('upload-success', file, {
                            status: 200,
                            body: {
                                location: result.location,
                                key: result.key,
                            },
                            uploadURL: result.location,
                        });
                        resolve();
                    },
                    onError: (err) => {
                        this.uppy.emit('upload-error', file, err);
                        reject(err);
                    },
                    onAbort: () => {
                        resolve(); // Normal completion, not an error
                    },
                });
                // Store uploader for external abort if needed
                this.#uploaders[file.id] = uploader;
                // Start the upload
                uploader.start();
            });
        }
        finally {
            // Clean up uploader instance after upload completes or fails
            delete this.#uploaders[file.id];
        }
    }
    #shouldUseMultipart(file) {
        const { shouldUseMultipart } = this.opts;
        if (typeof shouldUseMultipart === 'function') {
            return shouldUseMultipart(file);
        }
        if (typeof shouldUseMultipart === 'boolean') {
            return shouldUseMultipart;
        }
        // Default: multipart for files > 100MB
        return (file.size ?? 0) > 100 * MB;
    }
    #generateKey(file) {
        // in companion mode, no need to run the generateObjectKey function even if it's passed by the user,
        // because the key is generated on the server side and we need to remove the option to pass generateObjectKey in companion mode.
        if ('companionEndpoint' in this.opts) {
            return file.name;
        }
        return (this.opts.generateObjectKey?.(file) ??
            `${crypto.randomUUID()}-${file.name}`);
    }
    // --------------------------------------------------------------------------
    // Remote File Upload
    // --------------------------------------------------------------------------
    #getAllowedMeta(file) {
        const allowedMetaFields = getAllowedMetaFields(this.opts.allowedMetaFields, file.meta);
        return Object.fromEntries(allowedMetaFields.map((key) => [key, file.meta[key]]));
    }
    /**
     * Builds the request body sent to Companion's provider get endpoint.
     * Tells Companion to use its server-side S3 upload path.
     */
    #getCompanionClientArgs(file) {
        return {
            ...file.remote.body,
            protocol: 's3-multipart',
            size: file.data.size,
            metadata: this.#getAllowedMeta(file),
        };
    }
    async #uploadRemoteFile(file) {
        this.#setResumableUploadsCapability(false);
        const controller = new AbortController();
        const removedHandler = (removedFile) => {
            if (removedFile.id === file.id)
                controller.abort();
        };
        this.uppy.on('file-removed', removedHandler);
        try {
            await this.uppy
                .getRequestClientForFile(file)
                .uploadRemoteFile(file, this.#getCompanionClientArgs(file), {
                signal: controller.signal,
                getQueue: () => this.#queue,
            });
        }
        finally {
            this.uppy.off('file-removed', removedHandler);
        }
    }
}
